import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ThemeProvider, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupsIcon from "@mui/icons-material/Groups";
import { DateTime } from "luxon";

import api from "/imports/api/client";
import { applyBranding } from "/imports/libs/branding";
import { createWidgetTheme } from "/imports/ui/styles/theme";
import CheckoutTelrCard from "/imports/ui/views/CheckoutTelrCard";

// Mirrors the server's deadline formula (OpenGameService.computeCaptureDueAt)
// for the pre-auth ESTIMATE only — once the hold settles, game.captureDueAt is
// authoritative.
//
// Inputs are REAL UTC. Rendering them in the browser's clock put the capture an
// hour away from the booking time shown beside it whenever the viewer isn't in
// the club's zone, so everything here resolves in the CLUB's zone instead.
const HOLD_VALIDITY_DAYS = 5;
const CAPTURE_SAFETY_HOURS = 12;
const DEFAULT_TZ = "Asia/Dubai";

export function holdDeadline(game, timeZone) {
  const zone = timeZone || DEFAULT_TZ;
  if (game?.captureDueAt) return DateTime.fromISO(game.captureDueAt).setZone(zone);
  const now = DateTime.now().setZone(zone);
  const candidates = [now.plus({ days: HOLD_VALIDITY_DAYS }).minus({ hours: CAPTURE_SAFETY_HOURS })];
  if (game?.bookingStartAt) candidates.push(DateTime.fromISO(game.bookingStartAt).setZone(zone));
  const future = candidates.filter((d) => d.isValid && d > now);
  return future.length ? DateTime.min(...future) : now;
}

/** True when the hold is captured on the same day it was authorized — the
    "no charge today" opener is false in that case. */
export function capturesToday(dt) {
  return !!dt?.isValid && dt.hasSame(DateTime.now().setZone(dt.zoneName), "day");
}

export function formatDeadline(dt) {
  if (capturesToday(dt)) return `today at ${dt.toFormat("h:mm a")}`;
  return dt.toFormat("ccc, MMM d 'at' h:mm a");
}

// CheckoutTelrCard renders invoices; a hold has none — feed it the same shape
// with the hold amount as the "total" so the left pane (branding + booking
// details + amount) reads identically to the regular payment page.
export function holdCardData(data) {
  if (!data) return null;
  return {
    saas: data.saas,
    widgetColor: data.widgetColor,
    client: data.host,
    bookingDetails: data.bookingDetails,
    invoice: { total: data.game?.totalAmount },
    payments: []
  };
}

export default function CheckoutTelrHoldView() {
  const { holdToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const clubId = queryParams.get("clubId");
  const widgetSlug = sessionStorage.getItem("widget_slug");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  const theme = useMemo(() => createWidgetTheme({ primaryColor: data?.widgetColor || undefined }), [data?.widgetColor]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getTelrHold(holdToken);
        if (cancelled) return;
        setData(res);
        applyBranding({ name: res?.saas?.name, logoUrl: res?.saas?.logoUrl });
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load this card-hold link.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [holdToken]);

  const handleAuthorize = async () => {
    setError(null);
    try {
      const res = await api.initiateTelrHold(holdToken, clubId);
      if (!res?.order?.url) {
        setError("Unable to start the card authorization. Please try again or contact the venue.");
        return;
      }
      setRedirecting(true);
      window.location.href = res.order.url;
    } catch (err) {
      setError(err.message || "Unable to start the card authorization. Please try again or contact the venue.");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const game = data?.game;
  const currency = (data?.saas?.currency || "AED").toUpperCase();
  const clubZone = data?.saas?.timeZone;
  const total = (Number(game?.totalAmount) || 0).toFixed(2);
  const share = (Number(game?.shareAmount) || 0).toFixed(2);
  const gameUrl = widgetSlug && game?.bookishId ? `/${widgetSlug}/bookings/${game.bookishId}` : null;

  let rightPaneContent;
  if (!game) {
    rightPaneContent = (
      <Box textAlign="center">
        <Typography variant="h6" gutterBottom>
          This card-hold link is no longer active
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please contact the venue if you need assistance.
        </Typography>
      </Box>
    );
  } else if (game.status === "OPEN") {
    rightPaneContent = (
      <Box textAlign="center">
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "5rem",
            height: "5rem",
            borderRadius: "50%",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
            mb: 2
          }}
        >
          <CheckCircleIcon sx={{ fontSize: "2.75rem", color: "primary.main" }} />
        </Box>
        <Typography variant="h6" fontWeight={600} mb={1}>
          Card hold confirmed
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Your court is secured. Share your booking link so friends can claim their spots — unpaid spots will be charged to your card{" "}
          {formatDeadline(holdDeadline(game, clubZone))}.
        </Typography>
        {gameUrl && (
          <Button variant="contained" fullWidth onClick={() => navigate(gameUrl)}>
            View game & share link
          </Button>
        )}
      </Box>
    );
  } else if (game.status !== "PENDING_AUTH") {
    rightPaneContent = (
      <Box textAlign="center">
        <Typography variant="h6" gutterBottom>
          This card hold is no longer pending
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          There is nothing to authorize here anymore.
        </Typography>
        {gameUrl && (
          <Button variant="contained" fullWidth onClick={() => navigate(gameUrl)}>
            View booking
          </Button>
        )}
      </Box>
    );
  } else if (redirecting) {
    rightPaneContent = (
      <Box textAlign="center">
        <Typography variant="h6" gutterBottom>
          Redirecting to Telr payment page...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we securely connect you to the payment gateway.
        </Typography>
      </Box>
    );
  } else {
    rightPaneContent = (
      <Box width="100%">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <GroupsIcon color="primary" sx={{ flexShrink: 0 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: "0.9375rem", md: "1rem" } }}>
            Split booking — {game.seatsTotal} players
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2} sx={{ lineHeight: 1.6, fontSize: { xs: "0.8125rem", md: "0.875rem" } }}>
          {capturesToday(holdDeadline(game, clubZone)) ? "We place a hold of " : "No charge today — we place a hold of "}
          {currency} {total} on your card to secure the court. Each player pays {currency} {share} through your booking link.{" "}
          <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
            Unpaid spots will be charged to your card {formatDeadline(holdDeadline(game, clubZone))}.
          </Box>
        </Typography>
        {error && (
          <Typography variant="body2" color="error.main" mb={2}>
            {error}
          </Typography>
        )}
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleAuthorize}
          sx={{ py: 1.5, letterSpacing: { xs: 0.4, md: 1 }, fontSize: { xs: "0.875rem", md: "0.9375rem" } }}
        >
          AUTHORIZE CARD HOLD
        </Button>
      </Box>
    );
  }

  if (!data && error) {
    rightPaneContent = (
      <Box textAlign="center">
        <Typography variant="h6" color="error.main" gutterBottom>
          Could not load this card-hold link
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CheckoutTelrCard data={holdCardData(data)} rightPaneContent={rightPaneContent} />
    </ThemeProvider>
  );
}
